// Where the doctor publishes when they are free.
//
// Rules are weekly and recurring ("Tuesdays 2–4pm"); exceptions — a blocked
// slot, or a slot that takes more or fewer patients than usual — are separate
// override rows, so the weekly pattern stays readable no matter how many
// one-offs accumulate.
//
// Slot times are WALL-CLOCK in the clinic's own zone. Nothing here converts
// timezones; notes-c stores wall-clock too, so the comparison that decides
// whether a slot is full is plain equality.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import {
  applyStandardWeek,
  createBlockedDates,
  createOverride,
  createRule,
  deleteBlockedDates,
  deleteOverride,
  deleteRule,
  fetchAvailability,
  fetchBlockedDates,
  fetchConfig,
  fetchOverrides,
  saveConfig,
  updateRule,
} from "../../utils/appointmentsApi";

// 0=Sunday, matching Postgres EXTRACT(DOW), JS Date.getDay() and the
// day_of_week column. Do not reorder.
const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const hhmm = (t) => String(t || "").slice(0, 5);
const ymd = (d) => String(d || "").slice(0, 10);

const MON_FRI = [1, 2, 3, 4, 5];

// The clinic's own timezone — what "9:00" means for its appointments. NOT a
// user timezone: notes-c rows are converted INTO this one before a slot is
// judged full, so the schedule table and the booking grid agree.
const CLINIC_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "UTC",
];

const toMinutes = (t) => {
  const [h, m] = hhmm(t).split(":");
  return Number(h) * 60 + Number(m);
};

const fromMinutes = (n) =>
  `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

const pretty = (t) => {
  const mins = toMinutes(t);
  if (!Number.isFinite(mins)) return hhmm(t);
  const h24 = Math.floor(mins / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(mins % 60).padStart(2, "0")} ${h24 >= 12 ? "PM" : "AM"}`;
};

/**
 * When the LAST appointment of a window starts — null if none fits.
 *
 * "The day ends at 5" and "the last appointment is at 5" are different things,
 * and confusing them is the easiest way to publish a week that's 15 minutes
 * off. The editor shows this back so the doctor never has to do the sum.
 */
function lastSlotOf(start, end, slotMinutes) {
  const from = toMinutes(start);
  const to = toMinutes(end);
  const step = Number(slotMinutes);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(step > 0)) return null;
  const count = Math.floor((to - from) / step);
  return count >= 1 ? fromMinutes(from + (count - 1) * step) : null;
}

/**
 * Plain-English description of a window, or the reason it's impossible.
 *
 * Shared by the standard-week panel and the single-window editor so the two
 * cannot disagree about what a break means or where the last booking lands.
 * Mirrors the validation in api/appointment_slots.standard_week_rules — this
 * is the friendly copy, the server is the authority.
 */
function describeWindow({ days, startTime, endTime, breakStart, breakEnd }, step) {
  if (!days.length) return "Pick at least one day.";
  if (toMinutes(startTime) >= toMinutes(endTime)) {
    return "The day has to end after it starts.";
  }
  if (breakStart) {
    if (toMinutes(breakStart) >= toMinutes(breakEnd)) {
      return "The break has to end after it starts.";
    }
    if (
      toMinutes(breakStart) < toMinutes(startTime) ||
      toMinutes(breakEnd) > toMinutes(endTime)
    ) {
      return "The break has to fall inside the working day.";
    }
  }
  const firstHalfLast = breakStart
    ? lastSlotOf(startTime, breakStart, step)
    : null;
  const last = breakStart
    ? lastSlotOf(breakEnd, endTime, step)
    : lastSlotOf(startTime, endTime, step);
  const dayNames = days.map((d) => DAYS[d].slice(0, 3)).join(", ");
  const hours = breakStart
    ? `${pretty(startTime)}–${pretty(breakStart)} and ${pretty(breakEnd)}–${pretty(endTime)}`
    : `${pretty(startTime)}–${pretty(endTime)}`;
  if (!last && !firstHalfLast) {
    // With a break, "nothing fits" almost always means the break swallowed the
    // day — saying so is more useful than reporting the leftover hours.
    return breakStart
      ? "That break covers the whole day, leaving no appointments. " +
        "Remove the window instead if you're not working."
      : `${dayNames} · ${hours} — too short for a ${step}-minute appointment.`;
  }
  return (
    `${dayNames} · ${hours} · ${step}-minute appointments · ` +
    `last booking ${pretty(last || firstHalfLast)}`
  );
}

/** All the slot start times in [start, end), on the clinic's own grid. */
function slotsBetween(start, end, step) {
  const from = toMinutes(start);
  const to = toMinutes(end);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(step > 0)) return [];
  const out = [];
  for (let t = from; t + step <= to; t += step) out.push(fromMinutes(t));
  return out;
}

/**
 * Collapse consecutive per-slot overrides back into the ranges they came from.
 *
 * A range is stored as one row per slot — which keeps capacity precedence and
 * the unique indexes simple — but blocking 2–4pm would otherwise show as eight
 * identical "Blocked" lines. Rows merge only when they share a target and an
 * effect and sit exactly one slot apart, so two genuinely separate blocks stay
 * separate. Every merged row's id is kept, so Remove clears the whole range.
 */
function groupOverrides(overrides, step) {
  const key = (o) =>
    o.slot_date ? `d:${ymd(o.slot_date)}` : `w:${o.day_of_week}`;
  const sorted = [...(overrides || [])].sort(
    (a, b) =>
      key(a).localeCompare(key(b)) ||
      toMinutes(a.slot_time) - toMinutes(b.slot_time)
  );
  const groups = [];
  for (const o of sorted) {
    const prev = groups[groups.length - 1];
    const startsAt = toMinutes(o.slot_time);
    const contiguous =
      prev &&
      prev.key === key(o) &&
      Number(prev.capacity) === Number(o.capacity) &&
      toMinutes(prev.endTime) === startsAt;
    if (contiguous) {
      prev.endTime = fromMinutes(startsAt + step);
      prev.ids.push(o.id);
      prev.count += 1;
    } else {
      groups.push({
        key: key(o),
        slot_date: o.slot_date || null,
        day_of_week: o.slot_date ? null : o.day_of_week,
        capacity: o.capacity,
        reason: o.reason,
        startTime: hhmm(o.slot_time),
        endTime: fromMinutes(startsAt + step),
        ids: [o.id],
        count: 1,
      });
    }
  }
  return groups;
}

export default function AvailabilityTab({ schema }) {
  const toast = useToast();
  const [config, setConfig] = useState(null);
  const [rules, setRules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [draft, setDraft] = useState(null); // the "add a window" row
  const [week, setWeek] = useState({
    days: MON_FRI,
    startTime: "09:00",
    endTime: "17:00",
    lunch: false,
    lunchStart: "12:00",
    lunchEnd: "13:00",
  });
  const [editing, setEditing] = useState(null); // the window being edited
  const [blocked, setBlocked] = useState([]);
  const [addingTimeOff, setAddingTimeOff] = useState(false);
  const [addingException, setAddingException] = useState(false);
  const [timeOff, setTimeOff] = useState({
    fromDate: "",
    toDate: "",
    reason: "",
  });
  const [exception, setException] = useState({
    scope: "date",
    slotDate: "",
    dayOfWeek: 1,
    slotTime: "09:00",
    slotTimeEnd: "",
    capacity: 0,
    reason: "",
  });
  const [error, setError] = useState(null);

  // What the doctor will actually get, stated in the terms they think in.
  const weekPreview = useMemo(
    () =>
      config
        ? describeWindow(
            {
              days: week.days,
              startTime: week.startTime,
              endTime: week.endTime,
              breakStart: week.lunch ? week.lunchStart : null,
              breakEnd: week.lunch ? week.lunchEnd : null,
            },
            Number(config.slot_minutes)
          )
        : "",
    [config, week]
  );

  // Spells out how many days a range covers — "20th to 27th" is 8 days, not 7,
  // and getting that wrong closes a day of the clinic nobody meant to close.
  const timeOffPreview = useMemo(() => {
    if (!timeOff.fromDate) return "Pick a first day.";
    const to = timeOff.toDate || timeOff.fromDate;
    if (to < timeOff.fromDate) return "The last day can't be before the first.";
    const days =
      Math.round(
        (new Date(`${to}T00:00:00`) - new Date(`${timeOff.fromDate}T00:00:00`)) /
          86400000
      ) + 1;
    return days === 1
      ? `${timeOff.fromDate} closed — 1 day.`
      : `${timeOff.fromDate} to ${to} closed — ${days} days.`;
  }, [timeOff]);

  // Says how many slots a range will actually cover, on the clinic's own grid.
  const groupedOverrides = useMemo(
    () => (config ? groupOverrides(overrides, Number(config.slot_minutes)) : []),
    [overrides, config]
  );

  const exceptionPreview = useMemo(() => {
    if (!config) return "";
    const step = Number(config.slot_minutes);
    if (!exception.slotTimeEnd) {
      return `One ${step}-minute slot at ${pretty(exception.slotTime)}.`;
    }
    const times = slotsBetween(exception.slotTime, exception.slotTimeEnd, step);
    if (!times.length) {
      return `That range is shorter than one ${step}-minute appointment.`;
    }
    const effect =
      Number(exception.capacity) === 0
        ? "blocked"
        : `set to ${exception.capacity} patients`;
    return (
      `${pretty(exception.slotTime)}–${pretty(exception.slotTimeEnd)} · ` +
      `${times.length} slot${times.length === 1 ? "" : "s"} ${effect} ` +
      `(last ${pretty(times[times.length - 1])}).`
    );
  }, [config, exception]);

  const editPreview = useMemo(
    () =>
      config && editing
        ? describeWindow(
            {
              days: [editing.dayOfWeek],
              startTime: editing.startTime,
              endTime: editing.endTime,
              breakStart: editing.hasBreak ? editing.breakStart : null,
              breakEnd: editing.hasBreak ? editing.breakEnd : null,
            },
            Number(config.slot_minutes)
          )
        : "",
    [config, editing]
  );

  const reload = useCallback(async () => {
    const [cfg, rls, ovr, blk] = await Promise.all([
      fetchConfig(schema),
      fetchAvailability(schema),
      fetchOverrides(schema),
      // Tolerated separately: a clinic that hasn't run the blocked-dates
      // migration should still get a working availability editor.
      fetchBlockedDates(schema).catch(() => []),
    ]);
    setConfig(cfg.config);
    setRules(rls);
    setOverrides(ovr);
    setBlocked(blk);
    // Both editors PRE-FILL from the stored day (9–5 by default) rather than
    // opening blank. These are defaults for the boxes, not a second clamp
    // applied on top of whatever the doctor types.
    setDraft((d) =>
      d || {
        dayOfWeek: 1,
        startTime: cfg.config.day_start,
        endTime: cfg.config.day_end,
      }
    );
    setWeek((w) =>
      w.touched
        ? w
        : {
            ...w,
            // Reflect what's actually published: if the doctor works Tue/Thu,
            // the panel should open on Tue/Thu, not on Mon–Fri.
            days: rls.length
              ? [...new Set(rls.map((r) => Number(r.day_of_week)))].sort(
                  (a, b) => a - b
                )
              : MON_FRI,
            startTime: cfg.config.day_start,
            endTime: cfg.config.day_end,
          }
    );
  }, [schema]);

  useEffect(() => {
    reload().catch((e) => setError(e.message));
  }, [reload]);

  if (error) {
    return (
      <Text color="red.500">
        {error}
      </Text>
    );
  }
  if (!config || !draft) return <Spinner />;

  const run = async (fn, successMessage) => {
    try {
      await fn();
      if (successMessage) toast({ status: "success", title: successMessage });
    } catch (e) {
      toast({ status: "error", title: e.message });
    }
  };

  return (
    <Box>
      <Text fontWeight="bold" mb={2}>
        Slot settings
      </Text>
      <HStack spacing={4} mb={6} align="end" wrap="wrap">
        <Box>
          <FormLabel fontSize="sm" htmlFor="appt-slot-minutes">
            Slot length (min)
          </FormLabel>
          <Input
            id="appt-slot-minutes"
            type="number"
            width="120px"
            value={config.slot_minutes}
            onChange={(e) =>
              setConfig({ ...config, slot_minutes: e.target.value })
            }
          />
        </Box>
        {/* The day's hours live in "Standard week" below, not here — having
            two places to set them meant the one you edited might not be the
            one that took effect. */}
        <Box>
          <FormLabel fontSize="sm" htmlFor="appt-timezone">
            Clinic timezone
          </FormLabel>
          <Select
            id="appt-timezone"
            width="230px"
            value={config.timezone || "America/Los_Angeles"}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
          >
            {CLINIC_ZONES.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </Select>
        </Box>
        <Box>
          <FormLabel fontSize="sm" htmlFor="appt-capacity">
            Patients per slot
          </FormLabel>
          <Input
            id="appt-capacity"
            type="number"
            width="140px"
            value={config.default_capacity}
            onChange={(e) =>
              setConfig({ ...config, default_capacity: e.target.value })
            }
          />
        </Box>
        <Button
          colorScheme="blue"
          onClick={() =>
            run(async () => {
              await saveConfig({
                schema,
                slotMinutes: Number(config.slot_minutes),
                dayStart: config.day_start,
                dayEnd: config.day_end,
                defaultCapacity: Number(config.default_capacity),
                timezone: config.timezone,
              });
              await reload();
            }, "Settings saved")
          }
        >
          Save
        </Button>
      </HStack>

      {/* The standard week. Building Mon-Fri with a lunch break one window at
          a time is ten separate adds, so this generates them in one go — and
          it is also how you shift the whole week's hours. */}
      <Box borderWidth="1px" borderRadius="md" p={4} mb={6}>
        <Text fontWeight="bold" mb={1}>
          Standard week
        </Text>
        <Text fontSize="sm" color="gray.600" mb={3}>
          Replaces the weekly windows below.
        </Text>

        <Flex gap={2} wrap="wrap" mb={3}>
          {DAYS.map((label, i) => (
            <Checkbox
              key={label}
              isChecked={week.days.includes(i)}
              onChange={(e) =>
                setWeek({
                  ...week,
                  touched: true,
                  days: e.target.checked
                    ? [...week.days, i].sort((a, b) => a - b)
                    : week.days.filter((d) => d !== i),
                })
              }
            >
              {label.slice(0, 3)}
            </Checkbox>
          ))}
        </Flex>

        <HStack spacing={4} align="end" wrap="wrap" mb={3}>
          <Box>
            <FormLabel fontSize="sm" htmlFor="week-start">Day starts</FormLabel>
            <Input
              id="week-start"
              type="time"
              width="140px"
              value={week.startTime}
              onChange={(e) =>
                setWeek({ ...week, touched: true, startTime: e.target.value })
              }
            />
          </Box>
          <Box>
            <FormLabel fontSize="sm" htmlFor="week-end">Day ends</FormLabel>
            <Input
              id="week-end"
              type="time"
              width="140px"
              value={week.endTime}
              onChange={(e) =>
                setWeek({ ...week, touched: true, endTime: e.target.value })
              }
            />
          </Box>
          <Checkbox
            mb={2}
            isChecked={week.lunch}
            onChange={(e) =>
              setWeek({ ...week, touched: true, lunch: e.target.checked })
            }
          >
            Lunch break
          </Checkbox>
          {week.lunch && (
            <>
              <Box>
                <FormLabel fontSize="sm" htmlFor="lunch-start">From</FormLabel>
                <Input
                  id="lunch-start"
                  type="time"
                  width="140px"
                  value={week.lunchStart}
                  onChange={(e) =>
                    setWeek({ ...week, touched: true, lunchStart: e.target.value })
                  }
                />
              </Box>
              <Box>
                <FormLabel fontSize="sm" htmlFor="lunch-end">To</FormLabel>
                <Input
                  id="lunch-end"
                  type="time"
                  width="140px"
                  value={week.lunchEnd}
                  onChange={(e) =>
                    setWeek({ ...week, touched: true, lunchEnd: e.target.value })
                  }
                />
              </Box>
            </>
          )}
        </HStack>

        {/* Says back what will actually be bookable, so "ends at 5" never has
            to be mentally converted into "last appointment 4:45". */}
        <Text fontSize="sm" color="gray.600" mb={3} data-testid="week-preview">
          {weekPreview}
        </Text>

        <Button
          colorScheme="teal"
          size="sm"
          onClick={() =>
            run(async () => {
              const saved = await applyStandardWeek({
                schema,
                days: week.days,
                startTime: week.startTime,
                endTime: week.endTime,
                lunchStart: week.lunch ? week.lunchStart : null,
                lunchEnd: week.lunch ? week.lunchEnd : null,
              });
              setRules(saved);
              // Remember these hours as the clinic's default so the
              // "add a window" row pre-fills with what they actually work.
              await saveConfig({
                schema,
                slotMinutes: Number(config.slot_minutes),
                dayStart: week.startTime,
                dayEnd: week.endTime,
                defaultCapacity: Number(config.default_capacity),
                timezone: config.timezone,
              });
            }, "Weekly hours updated")
          }
        >
          Apply to my week
        </Button>
      </Box>

      <Text fontWeight="bold" mb={2}>
        Weekly availability
      </Text>
      <Table size="sm" mb={4}>
        <Thead>
          <Tr>
            <Th>Day</Th>
            <Th>From</Th>
            <Th>To</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {rules.map((r) => (
            <Tr key={r.id}>
              <Td>{DAYS[r.day_of_week]}</Td>
              <Td>{hhmm(r.start_time)}</Td>
              <Td>{hhmm(r.end_time)}</Td>
              <Td>
                <HStack spacing={1}>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={() =>
                      setEditing({
                        id: r.id,
                        dayOfWeek: Number(r.day_of_week),
                        startTime: hhmm(r.start_time),
                        endTime: hhmm(r.end_time),
                        hasBreak: false,
                        breakStart: "12:00",
                        breakEnd: "13:00",
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() =>
                      run(async () => setRules(await deleteRule(schema, r.id)))
                    }
                  >
                    Remove
                  </Button>
                </HStack>
              </Td>
            </Tr>
          ))}
          {rules.length === 0 && (
            <Tr>
              <Td colSpan={4}>
                <Text color="gray.500">
                  Your week is empty, so patients can't book anything. Use
                  "Standard week" above to publish your usual hours.
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      <Flex gap={2} align="end" wrap="wrap" mb={6}>
        <Select
          width="160px"
          aria-label="Day of week"
          value={draft.dayOfWeek}
          onChange={(e) =>
            setDraft({ ...draft, dayOfWeek: Number(e.target.value) })
          }
        >
          {DAYS.map((d, i) => (
            <option key={d} value={i}>
              {d}
            </option>
          ))}
        </Select>
        <Input
          type="time"
          width="140px"
          aria-label="Window start"
          value={draft.startTime}
          onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
        />
        <Input
          type="time"
          width="140px"
          aria-label="Window end"
          value={draft.endTime}
          onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
        />
        <Button
          colorScheme="teal"
          onClick={() =>
            run(async () => setRules(await createRule({ schema, ...draft })))
          }
        >
          Add window
        </Button>
      </Flex>

      {/* Editing one window. A break splits it into two, which is why this
          says "replaces this window with two" rather than pretending the row
          is being updated in place. */}
      {editing && (
        <Modal isOpen onClose={() => setEditing(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit this window</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormLabel fontSize="sm" htmlFor="edit-day">Day</FormLabel>
              <Select
                id="edit-day"
                mb={3}
                value={editing.dayOfWeek}
                onChange={(e) =>
                  setEditing({ ...editing, dayOfWeek: Number(e.target.value) })
                }
              >
                {DAYS.map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </Select>

              <HStack spacing={4} mb={3}>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="edit-start">Starts</FormLabel>
                  <Input
                    id="edit-start"
                    type="time"
                    value={editing.startTime}
                    onChange={(e) =>
                      setEditing({ ...editing, startTime: e.target.value })
                    }
                  />
                </Box>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="edit-end">Ends</FormLabel>
                  <Input
                    id="edit-end"
                    type="time"
                    value={editing.endTime}
                    onChange={(e) =>
                      setEditing({ ...editing, endTime: e.target.value })
                    }
                  />
                </Box>
              </HStack>

              <Checkbox
                mb={3}
                isChecked={editing.hasBreak}
                onChange={(e) =>
                  setEditing({ ...editing, hasBreak: e.target.checked })
                }
              >
                Add a break
              </Checkbox>
              {editing.hasBreak && (
                <HStack spacing={4} mb={3}>
                  <Box>
                    <FormLabel fontSize="sm" htmlFor="edit-break-start">
                      Break from
                    </FormLabel>
                    <Input
                      id="edit-break-start"
                      type="time"
                      value={editing.breakStart}
                      onChange={(e) =>
                        setEditing({ ...editing, breakStart: e.target.value })
                      }
                    />
                  </Box>
                  <Box>
                    <FormLabel fontSize="sm" htmlFor="edit-break-end">
                      Break to
                    </FormLabel>
                    <Input
                      id="edit-break-end"
                      type="time"
                      value={editing.breakEnd}
                      onChange={(e) =>
                        setEditing({ ...editing, breakEnd: e.target.value })
                      }
                    />
                  </Box>
                </HStack>
              )}

              <Text fontSize="sm" color="gray.600" data-testid="edit-preview">
                {editPreview}
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() =>
                  run(async () => {
                    setRules(
                      await updateRule({
                        schema,
                        id: editing.id,
                        dayOfWeek: editing.dayOfWeek,
                        startTime: editing.startTime,
                        endTime: editing.endTime,
                        breakStart: editing.hasBreak ? editing.breakStart : null,
                        breakEnd: editing.hasBreak ? editing.breakEnd : null,
                      })
                    );
                    setEditing(null);
                  }, "Window updated")
                }
              >
                Save window
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {addingTimeOff && (
        <Modal isOpen onClose={() => setAddingTimeOff(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add time off</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Closes these days completely. Leave the second date blank for a
                single day.
              </Text>
              <HStack spacing={4} mb={3}>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="off-from">First day</FormLabel>
                  <Input
                    id="off-from"
                    type="date"
                    value={timeOff.fromDate}
                    onChange={(e) =>
                      setTimeOff({ ...timeOff, fromDate: e.target.value })
                    }
                  />
                </Box>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="off-to">Last day</FormLabel>
                  <Input
                    id="off-to"
                    type="date"
                    value={timeOff.toDate}
                    onChange={(e) =>
                      setTimeOff({ ...timeOff, toDate: e.target.value })
                    }
                  />
                </Box>
              </HStack>
              <FormLabel fontSize="sm" htmlFor="off-reason">Reason</FormLabel>
              <Input
                id="off-reason"
                placeholder="Annual leave"
                value={timeOff.reason}
                onChange={(e) =>
                  setTimeOff({ ...timeOff, reason: e.target.value })
                }
              />
              <Text fontSize="sm" color="gray.600" mt={3} data-testid="timeoff-preview">
                {timeOffPreview}
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setAddingTimeOff(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isDisabled={!timeOff.fromDate}
                onClick={() =>
                  run(async () => {
                    setBlocked(
                      await createBlockedDates({ schema, ...timeOff })
                    );
                    setAddingTimeOff(false);
                    setTimeOff({ fromDate: "", toDate: "", reason: "" });
                  }, "Time off added")
                }
              >
                Add
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {addingException && (
        <Modal isOpen onClose={() => setAddingException(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add an exception</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Changes ONE slot. To close whole days, use Time off instead.
              </Text>

              <FormLabel fontSize="sm" htmlFor="exc-scope">Applies to</FormLabel>
              <Select
                id="exc-scope"
                mb={3}
                value={exception.scope}
                onChange={(e) =>
                  setException({ ...exception, scope: e.target.value })
                }
              >
                <option value="date">One date</option>
                <option value="weekday">Every week</option>
              </Select>

              {exception.scope === "date" ? (
                <>
                  <FormLabel fontSize="sm" htmlFor="exc-date">Date</FormLabel>
                  <Input
                    id="exc-date"
                    type="date"
                    mb={3}
                    value={exception.slotDate}
                    onChange={(e) =>
                      setException({ ...exception, slotDate: e.target.value })
                    }
                  />
                </>
              ) : (
                <>
                  <FormLabel fontSize="sm" htmlFor="exc-day">Day</FormLabel>
                  <Select
                    id="exc-day"
                    mb={3}
                    value={exception.dayOfWeek}
                    onChange={(e) =>
                      setException({
                        ...exception,
                        dayOfWeek: Number(e.target.value),
                      })
                    }
                  >
                    {DAYS.map((d, i) => (
                      <option key={d} value={i}>{d}</option>
                    ))}
                  </Select>
                </>
              )}

              <HStack spacing={4} mb={3}>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="exc-time">From</FormLabel>
                  <Input
                    id="exc-time"
                    type="time"
                    value={exception.slotTime}
                    onChange={(e) =>
                      setException({ ...exception, slotTime: e.target.value })
                    }
                  />
                </Box>
                <Box>
                  <FormLabel fontSize="sm" htmlFor="exc-time-end">
                    Until
                  </FormLabel>
                  <Input
                    id="exc-time-end"
                    type="time"
                    value={exception.slotTimeEnd}
                    onChange={(e) =>
                      setException({ ...exception, slotTimeEnd: e.target.value })
                    }
                  />
                </Box>
              </HStack>
              <Text fontSize="sm" color="gray.600" mb={3} data-testid="exception-preview">
                {exceptionPreview}
              </Text>

              <FormLabel fontSize="sm" htmlFor="exc-capacity">Effect</FormLabel>
              <Select
                id="exc-capacity"
                mb={3}
                value={exception.capacity}
                onChange={(e) =>
                  setException({ ...exception, capacity: Number(e.target.value) })
                }
              >
                <option value={0}>Blocked — nobody can book it</option>
                <option value={1}>1 patient</option>
                <option value={2}>2 patients</option>
                <option value={3}>3 patients</option>
                <option value={4}>4 patients</option>
              </Select>

              <FormLabel fontSize="sm" htmlFor="exc-reason">Reason</FormLabel>
              <Input
                id="exc-reason"
                placeholder="Double-booked for a follow-up"
                value={exception.reason}
                onChange={(e) =>
                  setException({ ...exception, reason: e.target.value })
                }
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setAddingException(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isDisabled={
                  exception.scope === "date" && !exception.slotDate
                }
                onClick={() =>
                  run(async () => {
                    setOverrides(
                      await createOverride({
                        schema,
                        // Exactly one target — the table's CHECK refuses both.
                        slotDate:
                          exception.scope === "date" ? exception.slotDate : null,
                        dayOfWeek:
                          exception.scope === "date" ? null : exception.dayOfWeek,
                        slotTime: exception.slotTime,
                        slotTimeEnd: exception.slotTimeEnd || null,
                        capacity: exception.capacity,
                        reason: exception.reason || null,
                      })
                    );
                    setAddingException(false);
                  }, "Exception added")
                }
              >
                Add
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Time off — whole days, as a range. A week away expressed as per-slot
          exceptions would be 160 rows at the default grid. */}
      <Flex align="center" gap={3} mb={2}>
        <Text fontWeight="bold">Time off</Text>
        <Button size="xs" onClick={() => setAddingTimeOff(true)}>
          + Add time off
        </Button>
      </Flex>
      <Table size="sm" mb={6}>
        <Thead>
          <Tr>
            <Th>Dates</Th>
            <Th>Reason</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {blocked.map((b) => (
            <Tr key={b.id}>
              <Td>
                {ymd(b.from_date) === ymd(b.to_date)
                  ? ymd(b.from_date)
                  : `${ymd(b.from_date)} → ${ymd(b.to_date)}`}
              </Td>
              <Td>{b.reason || "—"}</Td>
              <Td>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    run(async () =>
                      setBlocked(await deleteBlockedDates(schema, b.id))
                    )
                  }
                >
                  Remove
                </Button>
              </Td>
            </Tr>
          ))}
          {blocked.length === 0 && (
            <Tr>
              <Td colSpan={3}>
                <Text color="gray.500">None.</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      <Flex align="center" gap={3} mb={2}>
        <Text fontWeight="bold">Exceptions</Text>
        <Button size="xs" onClick={() => setAddingException(true)}>
          + Add exception
        </Button>
      </Flex>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>When</Th>
            <Th>Time</Th>
            <Th>Effect</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {groupedOverrides.map((g) => (
            <Tr key={`${g.key}-${g.startTime}`}>
              <Td>
                {g.slot_date ? ymd(g.slot_date) : `Every ${DAYS[g.day_of_week]}`}
              </Td>
              <Td>
                {/* A single slot reads as a time; a range reads as a range. */}
                {g.count === 1
                  ? pretty(g.startTime)
                  : `${pretty(g.startTime)}–${pretty(g.endTime)}`}
              </Td>
              {/* capacity 0 is a block; anything else is a capacity change. */}
              <Td>
                {Number(g.capacity) === 0 ? "Blocked" : `${g.capacity} spots`}
                {g.count > 1 && (
                  <Text as="span" color="gray.500" fontSize="xs">
                    {` · ${g.count} slots`}
                  </Text>
                )}
              </Td>
              <Td>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    run(async () => {
                      // Remove clears the whole range it displays, not just
                      // the first slot of it.
                      let latest = overrides;
                      for (const id of g.ids) {
                        latest = await deleteOverride(schema, id);
                      }
                      setOverrides(latest);
                    })
                  }
                >
                  Remove
                </Button>
              </Td>
            </Tr>
          ))}
          {overrides.length === 0 && (
            <Tr>
              <Td colSpan={4}>
                <Text color="gray.500">None.</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
